<svelte:options runes={true} />

<script lang="ts">
  import { Button } from "bits-ui";
  import type { Snippet, ComponentProps } from "svelte";

  export type ButtonVariant = "primary" | "secondary" | "tertiary" | "link";
  export type ButtonSize = "sm" | "md" | "lg" | "xl";
  export type ButtonColor = "charcoal" | "sand" | "white" | "destructive";

  /**
   * Discriminated union of valid variant+color combinations per the Figma design system.
   * Use this type when you need compile-time safety for the supported pairs.
   */
  export type ButtonAppearance =
    | { variant?: "primary"; color?: "charcoal" | "sand" | "white" | "destructive" }
    | { variant: "secondary" | "tertiary"; color?: "charcoal" | "white" }
    | { variant: "link"; color?: "charcoal" | "destructive" | "white" };

  type OwnProps = ButtonAppearance & {
    size?: ButtonSize;
    disabled?: boolean;
    href?: string;
    target?: string;
    rel?: string;
    class?: string;
    /** Required when `iconOnly` is true to provide an accessible label. */
    "aria-label"?: string;
    onclick?: (e: MouseEvent) => void;
    children?: Snippet;
    leadingIcon?: Snippet;
    /** Not rendered when `iconOnly` is true. */
    trailingIcon?: Snippet;
    iconOnly?: boolean;
  };

  type Props = OwnProps &
    Omit<ComponentProps<typeof Button.Root>, keyof OwnProps | "ref" | "child" | "type">;

  let {
    variant = "primary",
    size = "md",
    color = "charcoal",
    disabled,
    href,
    target,
    rel,
    class: className = "",
    "aria-label": ariaLabel,
    onclick,
    children,
    leadingIcon,
    trailingIcon,
    iconOnly = false,
    ...restProps
  }: Props = $props();

  // Dev-only guard: no state mutation — console.warn is a pure side-effect.
  if (import.meta.env.DEV) {
    $effect(() => {
      if (iconOnly && !ariaLabel) {
        console.warn("[Button] `aria-label` is required when `iconOnly` is true.");
      }
    });
  }
</script>

<Button.Root
  {...restProps}
  {disabled}
  onclick={href != null && disabled ? undefined : onclick}
  aria-label={ariaLabel}
  {...href != null ? { href, target, rel } : {}}
  class={[
    "da-button",
    `da-button--${variant}`,
    `da-button--${size}`,
    color !== "charcoal" ? `da-button--${color}` : null,
    iconOnly ? "da-button--icon-only" : null,
    className,
  ]}
>
  {#if leadingIcon}{@render leadingIcon()}{/if}
  {#if !iconOnly && children}{@render children()}{/if}
  {#if !iconOnly && trailingIcon}{@render trailingIcon()}{/if}
</Button.Root>

<style>
  @layer aurora {
    /*
     * Internal theming custom properties — scoped to .da-button.
     * Variant and color modifier rules override these so they remain as
     * simple as possible.
     */
    :global(.da-button) {
      --_bg: transparent;
      --_fg: var(--da-color-charcoal-500);
      --_border-width: 0px;
      --_border-color: transparent;
      --_hover-bg: transparent;
      --_hover-border: transparent;
      /*
       * Setting --_hover-fg to `initial` makes it so that
       * var(--_hover-fg, var(--_fg)) always falls back to --_fg unless a
       * variant rule supplies a real value. This avoids the need to reset
       * hover colour in every variant that doesn't change it.
       */
      --_hover-fg: initial;
      --_hover-shadow: none;
      --_focus-ring: var(--da-color-charcoal-500);
      --_focus-shadow: none;

      -webkit-tap-highlight-color: transparent;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: var(--da-space-2);
      border-radius: var(--da-radius-xl);
      font-family: var(--da-font-family-primary);
      font-weight: var(--da-font-weight-semibold);
      text-transform: uppercase;
      cursor: pointer;
      line-height: 1;
      background: var(--_bg);
      color: var(--_fg);
      border: var(--_border-width) solid var(--_border-color);
      transition:
        background var(--da-duration-normal) var(--da-ease-out),
        color var(--da-duration-normal) var(--da-ease-out),
        border-color var(--da-duration-normal) var(--da-ease-out),
        box-shadow var(--da-duration-normal) var(--da-ease-out),
        transform var(--da-duration-fast) var(--da-ease-out);

      &:hover:global(:not(:disabled)) {
        background: var(--_hover-bg);
        border-color: var(--_hover-border);
        color: var(--_hover-fg, var(--_fg));
        box-shadow: var(--_hover-shadow);
      }

      &:focus {
        outline: none;
      }

      &:focus-visible {
        outline: 2px solid var(--_focus-ring);
        outline-offset: 2px;
        box-shadow: var(--_focus-shadow);
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        box-shadow: none;
      }

      /* A Button used as a listbox option (search autosuggest) never takes DOM
         focus — aria-activedescendant moves the highlight instead — so it needs
         the focus-visible ring rendered from the ARIA state. */
      &[aria-selected="true"] {
        outline: 2px solid var(--_focus-ring);
        outline-offset: 2px;
        box-shadow: var(--_focus-shadow);
      }
    }

    /* ---- Hierarchy variants ---- */

    :global(.da-button--primary) {
      --_bg: var(--da-color-charcoal-500);
      --_fg: var(--da-color-white);
      --_border-width: 1px;
      --_border-color: var(--da-color-charcoal-500);
      --_hover-bg: var(--da-color-charcoal-700);
      --_hover-border: var(--da-color-charcoal-700);
      --_hover-shadow: var(--da-shadow-md);
    }

    :global(.da-button--secondary) {
      --_border-width: 1.5px;
      --_border-color: var(--da-color-charcoal-500);
      --_hover-bg: var(--da-color-brand-sand-100);
      --_hover-shadow: var(--da-shadow-md);
    }

    :global(.da-button--tertiary) {
      --_hover-bg: var(--da-color-charcoal-25);
    }

    :global(.da-button--link) {
      --_hover-fg: var(--da-color-charcoal-700);
      text-decoration: underline;
      text-underline-offset: 3px;
      border-radius: 2px;
      text-transform: none;

      &:focus-visible {
        outline-offset: 4px;
      }
    }

    /* ---- Active scale ---- */

    /* Tertiary-weight and icon-only: snappier press */
    :global(.da-button:not(.da-button--link):active:not(:disabled)) {
      transform: scale(0.9);
    }

    /* Primary/secondary text buttons: softer press befitting a heavier action */
    :global(.da-button--primary:not(.da-button--icon-only):active:not(:disabled)),
    :global(.da-button--secondary:not(.da-button--icon-only):active:not(:disabled)) {
      transform: scale(0.95);
    }

    @media (prefers-reduced-motion: reduce) {
      :global(.da-button:active) {
        transform: none;
      }
    }

    /* ---- Sizes ---- */

    :global(.da-button--sm) {
      padding: var(--da-space-2) var(--da-space-3);
      font-size: var(--da-font-size-xs);
      font-weight: var(--da-font-weight-medium); /* sm uses medium, others inherit semibold */
    }

    :global(.da-button--md) {
      padding: var(--da-space-px-10) var(--da-space-4);
      font-size: var(--da-font-size-sm);
    }

    :global(.da-button--lg) {
      padding: var(--da-space-px-10) var(--da-space-4);
      font-size: var(--da-font-size-md);
      letter-spacing: var(--da-letter-spacing-display);
    }

    :global(.da-button--xl) {
      padding: var(--da-space-3) var(--da-space-5);
      font-size: var(--da-font-size-md);
      letter-spacing: var(--da-letter-spacing-display);
    }

    /* ---- Icon-only ---- */

    /* Shared: zero inline padding. Compound selectors below set the square width. */
    :global(.da-button--icon-only) {
      padding-inline: 0;
    }
    :global(.da-button--icon-only.da-button--sm) {
      width: 32px;
      height: 32px;
    }
    :global(.da-button--icon-only.da-button--md) {
      width: 40px;
      height: 40px;
    }
    :global(.da-button--icon-only.da-button--lg) {
      width: 48px;
      height: 48px;
    }
    :global(.da-button--icon-only.da-button--xl) {
      width: 56px;
      height: 56px;
    }

    /* Link resets size-imposed dimensions — compound selector wins over single-class rules */
    :global(.da-button--link.da-button--sm),
    :global(.da-button--link.da-button--md),
    :global(.da-button--link.da-button--lg),
    :global(.da-button--link.da-button--xl) {
      padding: 0;
      letter-spacing: normal;
    }

    /* ---- Color variants (custom properties only — all behaviour handled by base) ---- */

    :global(.da-button--primary.da-button--sand) {
      --_bg: var(--da-color-brand-sand-500);
      --_fg: var(--da-color-charcoal-500);
      --_border-color: var(--da-color-brand-sand-500);
      --_hover-bg: var(--da-color-brand-sand-700);
      --_hover-border: var(--da-color-brand-sand-700);
    }

    /* Dual focus ring: white outline (dark bg) + charcoal shadow (light bg) */
    :global(.da-button--primary.da-button--white) {
      --_bg: var(--da-color-white);
      --_fg: var(--da-color-charcoal-500);
      --_border-color: var(--da-color-white);
      --_hover-bg: var(--da-color-brand-sand-50);
      --_hover-border: var(--da-color-brand-sand-50);
      --_focus-ring: var(--da-color-white);
      --_focus-shadow: 0 0 0 4px var(--da-color-charcoal-500);
    }

    :global(.da-button--primary.da-button--destructive) {
      --_bg: var(--da-color-red-500);
      --_fg: var(--da-color-white);
      --_border-color: var(--da-color-red-500);
      --_hover-bg: var(--da-color-red-700);
      --_hover-border: var(--da-color-red-700);
      --_focus-ring: var(--da-color-red-500);
    }

    :global(.da-button--secondary.da-button--white) {
      --_fg: var(--da-color-white);
      --_border-color: var(--da-color-white);
      --_hover-bg: rgb(255 255 255 / 0.1);
      --_focus-ring: var(--da-color-white);
    }

    :global(.da-button--tertiary.da-button--white) {
      --_fg: var(--da-color-white);
      --_hover-bg: rgb(255 255 255 / 0.1);
      --_focus-ring: var(--da-color-white);
    }

    :global(.da-button--link.da-button--destructive) {
      --_fg: var(--da-color-red-500);
      --_hover-fg: var(--da-color-red-700);
      --_focus-ring: var(--da-color-red-500);
    }

    :global(.da-button--link.da-button--white) {
      --_fg: var(--da-color-white);
      --_hover-fg: rgb(255 255 255 / 0.8);
      --_focus-ring: var(--da-color-white);
    }
  }
</style>
